import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDummyData } from "../../../contexts/DummyDataContext";
import { useBook } from "../../../contexts/BookContext";
import {
  generateStory,
  checkStoryStatus,
} from "../../../services/storyGenerationService";
import { generateWholeStory } from "../../../services/bookService/operations/generateWholeStory";
import { updateBookWithPreview } from '../../../services/bookService/operations/updateBookWithPreview';
import { getBook } from '../../../services/bookService/operations/getBook';
import { notify, toastIds } from '../../../utils/notify';
import { useStatusTracking } from '../../../contexts/StatusTrackingContext';
import { doc, collection, query, where, orderBy, limit, getDocs, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ACTIVE_DESIGN_STATUSES } from "../../../constants/designStatuses";

export function useEditPage(bookId) {
  const navigate = useNavigate();
  const { isDummyDataEnabled } = useDummyData();
  const { currentBook, updateBook } = useBook();
  const [showPreview, setShowPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageContents, setPageContents] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [canGoToDesign, setCanGoToDesign] = useState(false);
  const [operationType, setOperationType] = useState(null); // 'preview' or 'design'
  const [webhookId, setWebhookId] = useState(null);
  const [selectedStyles, setSelectedStyles] = useState({
    bookStyle: null,
    visualStyle: null,
    writingStyle: null,
  });
  const [wordsPerPage, setWordsPerPage] = useState(50);
  const [storyProgressId, setStoryProgressId] = useState(null);
  const [storyProgressStatus, setStoryProgressStatus] = useState(null);
  const [storyProgressCreatedAt, setStoryProgressCreatedAt] = useState(null);
  const [loadingUIMessage, setLoadingUIMessage] = useState({progress:0,text:'Preparing Request...'});
  const [draftedResult, setDraftedResult] = useState(null);
  
  // State for tracking book preview processing
  const [previewProcessingId, setPreviewProcessingId] = useState(null);
  const [previewProcessingCreatedAt, setPreviewProcessingCreatedAt] = useState(null);
  
  // Get processing status from BookContext (single source of truth)
  const { processingStatus: previewProcessingStatus, setProcessingStatus, setLoadingUIMessage: setGlobalLoadingUIMessage } = useBook();
  // True when a design operation was already in progress when this page mounted
  // (i.e. the user navigated back to /edit mid-generation) - used to restore the
  // loading overlay in its minimized state instead of the full-screen modal
  const designActiveAtMountRef = useRef(ACTIVE_DESIGN_STATUSES.has(previewProcessingStatus));
  const [cancelTimerReached, setCancelTimerReached] = useState(false);
  const [storyCancelTimerReached, setStoryCancelTimerReached] = useState(false);
  const [isSavingBasicInfo, setIsSavingBasicInfo] = useState(false);
  const { updateBookProcessingStatus } = useStatusTracking();

  // Reset preview-related state whenever the book changes to avoid showing
  // stale previews from a previously loaded book.
  useEffect(() => {
    setShowPreview(false);
    setCurrentPage(1);
    setPageContents([]);
    setDraftedResult(null);
    setStoryProgressId(null);
    setStoryProgressStatus(null);
    setStoryProgressCreatedAt(null);
    setPreviewProcessingId(null);
    setPreviewProcessingCreatedAt(null);
    setIsProcessing(false);
    setOperationType(null);
    setCanGoToDesign(false);
  }, [bookId]);

  const toMillisValue = (createdAt) => {
    if (!createdAt) return null;
    if (typeof createdAt === 'number') return createdAt;
    if (typeof createdAt === 'string') {
      const parsed = Date.parse(createdAt);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (typeof createdAt?.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
    return null;
  };
  
  // Use refs to track listeners and prevent memory leaks
  const isMountedRef = useRef(true);
  const activeListenerRef = useRef(null);
  const previewProcessingListenerRef = useRef(null);
  const didLogIncompleteAgeOnceRef = useRef(false);

  useEffect(() => {
    if (!bookId) return;
    if (didLogIncompleteAgeOnceRef.current) return;
    didLogIncompleteAgeOnceRef.current = true;

    const logOnce = async () => {
      try {
        const now = Date.now();

        const storyQuery = query(
          collection(db, 'storyProgress'),
          where('bookId', '==', bookId),
          where('status', 'in', ['pending', 'outlineGenerated', 'almostThere', 'processing']),
          orderBy('createdAt', 'desc'),
          limit(1)
        );

        const previewQuery = query(
          collection(db, 'bookWithPreviewProcessing'),
          where('bookId', '==', bookId),
          where('status', 'not-in', ['completed', 'error', 'failed', 'cancelled']),
          orderBy('createdAt', 'desc'),
          limit(1)
        );

        const [storySnap, previewSnap] = await Promise.all([
          getDocs(storyQuery),
          getDocs(previewQuery),
        ]);

        if (!storySnap.empty) {
          const d = storySnap.docs[0].data();
          const createdMs = toMillisValue(d.createdAt);
          if (createdMs) {
            console.log(`[EditPage] storyProgress not completed for ~${Math.round((now - createdMs) / 1000)}s`, {
              bookId,
              status: d.status,
              storyProgressId: storySnap.docs[0].id,
            });
          } else {
            console.log('[EditPage] storyProgress incomplete (createdAt missing/unparseable)', {
              bookId,
              status: d.status,
              storyProgressId: storySnap.docs[0].id,
            });
          }
        }

        if (!previewSnap.empty) {
          const d = previewSnap.docs[0].data();
          const createdMs = toMillisValue(d.createdAt);
          if (createdMs) {
            console.log(`[EditPage] bookWithPreviewProcessing not completed for ~${Math.round((now - createdMs) / 1000)}s`, {
              bookId,
              status: d.status,
              processingId: previewSnap.docs[0].id,
            });
          } else {
            console.log('[EditPage] bookWithPreviewProcessing incomplete (createdAt missing/unparseable)', {
              bookId,
              status: d.status,
              processingId: previewSnap.docs[0].id,
            });
          }
        }
      } catch (e) {
        console.error('[EditPage] Failed to compute incomplete age on first load', e);
      }
    };

    logOnce();
  }, [bookId]);

  const CANCEL_TIMEOUT_MS = 6 * 60 * 1000; // 6 minutes

  // Timer to reactively show cancel button after 6 minutes
  useEffect(() => {
    setCancelTimerReached(false);
    if (!previewProcessingId || !previewProcessingCreatedAt) return;
    if (['completed', 'error', 'failed', 'cancelled'].includes(previewProcessingStatus)) return;
    const createdMs = toMillisValue(previewProcessingCreatedAt);
    if (!createdMs) return;
    const elapsed = Date.now() - createdMs;
    if (elapsed >= CANCEL_TIMEOUT_MS) {
      setCancelTimerReached(true);
      return;
    }
    const remaining = CANCEL_TIMEOUT_MS - elapsed;
    const timer = setTimeout(() => setCancelTimerReached(true), remaining);
    return () => clearTimeout(timer);
  }, [previewProcessingId, previewProcessingCreatedAt, previewProcessingStatus]);

  const canCancelDesignProcessing = (() => {
    if (!previewProcessingId) return false;
    if (operationType !== 'design') return false;
    if (['completed', 'error', 'failed', 'cancelled'].includes(previewProcessingStatus)) return false;
    return cancelTimerReached;
  })();

  // Timer to reactively show cancel button for story generation after 6 minutes
  useEffect(() => {
    setStoryCancelTimerReached(false);
    if (!storyProgressId || !storyProgressCreatedAt) return;
    if (['completed', 'error', 'failed', 'cancelled'].includes(storyProgressStatus)) return;
    const createdMs = toMillisValue(storyProgressCreatedAt);
    if (!createdMs) return;
    const elapsed = Date.now() - createdMs;
    if (elapsed >= CANCEL_TIMEOUT_MS) {
      setStoryCancelTimerReached(true);
      return;
    }
    const remaining = CANCEL_TIMEOUT_MS - elapsed;
    const timer = setTimeout(() => setStoryCancelTimerReached(true), remaining);
    return () => clearTimeout(timer);
  }, [storyProgressId, storyProgressCreatedAt, storyProgressStatus]);

  const canCancelStoryGeneration = (() => {
    if (!storyProgressId) return false;
    if (operationType !== 'preview') return false;
    if (['completed', 'error', 'failed', 'cancelled'].includes(storyProgressStatus)) return false;
    return storyCancelTimerReached;
  })();

  const cancelPreviewProcessing = useCallback(async () => {
    if (!previewProcessingId) return;
    try {
      if (previewProcessingListenerRef.current) {
        previewProcessingListenerRef.current();
        previewProcessingListenerRef.current = null;
      }
      await updateDoc(doc(db, 'bookWithPreviewProcessing', previewProcessingId), {
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
      });
      setCanGoToDesign(false);
      setIsProcessing(false);
      setOperationType(null);
      notify.info('Cancelled preview processing', { toastId: `edit-cancel-preview-${previewProcessingId}` });
    } catch (e) {
      console.error('[useEditPage] cancelPreviewProcessing error', e);
      notify.error('Failed to cancel', { toastId: `edit-cancel-preview-error-${previewProcessingId}` });
    }
  }, [previewProcessingId]);

  const cancelStoryGeneration = useCallback(async () => {
    if (!storyProgressId) return;
    try {
      if (activeListenerRef.current) {
        activeListenerRef.current();
        activeListenerRef.current = null;
      }
      await updateDoc(doc(db, 'storyProgress', storyProgressId), {
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
      });
      setStoryProgressStatus('cancelled');
      setIsProcessing(false);
      setOperationType(null);
      notify.info('Cancelled story generation', { toastId: `edit-cancel-story-${storyProgressId}` });
    } catch (e) {
      console.error('[useEditPage] cancelStoryGeneration error', e);
      notify.error('Failed to cancel', { toastId: `edit-cancel-story-error-${storyProgressId}` });
    }
  }, [storyProgressId]);

  // Combined cancel capability for both flows
  const canCancelProcessing = canCancelDesignProcessing || canCancelStoryGeneration;
  const handleCancelProcessing = useCallback(async () => {
    if (canCancelStoryGeneration) return cancelStoryGeneration();
    if (canCancelDesignProcessing) return cancelPreviewProcessing();
  }, [canCancelStoryGeneration, canCancelDesignProcessing, cancelStoryGeneration, cancelPreviewProcessing]);

  // Force-refresh the book from server, then navigate
  const refreshBookThenNavigate = useCallback(async () => {
    try {
      const updated = await getBook(bookId);
      if (updated) {
        await updateBook(updated.id, updated);
      }
      // If world still missing, wait briefly for realtime write
      let hasWorld = !!(updated && updated.world);
      if (!hasWorld && bookId) {
        console.log('[useEditPage] waiting for world via onSnapshot');
        await new Promise((resolve) => {
          const ref = doc(db, 'books', bookId);
          const timer = setTimeout(() => {
            if (unsub) unsub();
            resolve(null);
          }, 2000);
          let unsub = onSnapshot(ref, (snap) => {
            const d = snap.exists() ? snap.data() : null;
            if (d && d.world) {
              try { updateBook(bookId, { world: d.world }); } catch (_) {}
              clearTimeout(timer);
              if (unsub) unsub();
              resolve(d.world);
            }
          });
        });
      }
    } catch (e) {
      console.error('[useEditPage] refreshBookThenNavigate error', e);
    } finally {
      navigate(`/world/${bookId}`);
    }
  }, [bookId, navigate, updateBook, db]);
  
  // Function to dispatch error event for the modal
  const showErrorModal = useCallback((title, message) => {
    const event = new CustomEvent('storyGenerationError', {
      detail: { title, message }
    });
    window.dispatchEvent(event);
  }, []);

  const handleStyleSelect = useCallback((type, style) => {
    setSelectedStyles((prev) => ({
      ...prev,
      [type]: style,
    }));
  }, []);

  // Calculate words per page based on selected book style
  const wordsPerPageValue = selectedStyles.bookStyle?.wordsPerPage || 50;

  // Function to update loading UI messages based on story progress
    const loadingUIMessagesForStoryGeneration = (status) => {
      switch(status){
        case 'pending':
          setLoadingUIMessage({progress: 10, text: 'Calling Flux Prompt....'})
          break;
        case 'outlineGenerated':
          setLoadingUIMessage({progress: 40, text: 'Flux Prompt is Generated Story Outline Successfully....'})
          break;
          case 'almostThere':
          setLoadingUIMessage({progress: 70, text: 'Almost there \n Cleaning Up Story....'})
          break;
        case 'completed':
          setLoadingUIMessage({progress: 0, text: 'Generation Completed....'})
          break;
        default:
          setLoadingUIMessage({progress: 0, text: 'Preparing Request...'})
      }
    }
 
  // Load book preview data if available when component mounts
  useEffect(() => {
    if (!currentBook) return;
    
    // First priority: Check if the book has basic storyContent that we can display
    if (currentBook.storyContent && currentBook.storyContent.trim()) {
      console.log('Loading preview from storyContent field:', currentBook.storyContent.substring(0, 100) + '...');
      
      // Split the story content into pages based on total pages
      const totalPages = currentBook.totalPages || 32;
      const storyText = currentBook.storyContent.trim();
      
      // Split story into sentences and distribute across pages
      const sentences = storyText.split(/[.!?]+/).filter(s => s.trim().length > 0);
      const contents = new Array(totalPages).fill("");
      
      // Distribute sentences across pages more evenly
      const sentencesPerPage = Math.ceil(sentences.length / totalPages);
      
      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const startSentence = pageIndex * sentencesPerPage;
        const endSentence = Math.min(startSentence + sentencesPerPage, sentences.length);
        
        if (startSentence < sentences.length) {
          const pageSentences = sentences.slice(startSentence, endSentence);
          contents[pageIndex] = pageSentences.join('. ').trim() + (pageSentences.length > 0 ? '.' : '');
        }
      }
      
      console.log('Setting page contents from storyContent field, pages with content:', contents.filter(c => c.trim()).length);
      setPageContents(contents);
      setShowPreview(true);
      setCurrentPage(1);
      // don't return yet there might be a spread object
    }
    
    // Load spreads from the book (spreads is an object with keys like "spread-1", "spread-2", etc.)
    if (currentBook.spreads && typeof currentBook.spreads === 'object') {
      console.log('Loading spreads from book:', currentBook.id, currentBook.spreads);
      
      // Determine the total number of spreads (excluding spread-0 which is the cover)
      const spreadKeys = Object.keys(currentBook.spreads)
        .filter(key => key !== 'spread-0')
        .sort((a, b) => {
          // Extract numbers from spread-X format and compare
          const numA = parseInt(a.split('-')[1]);
          const numB = parseInt(b.split('-')[1]);
          return numA - numB;
        });
      
      console.log('Ordered spread keys:', spreadKeys);
      
      // Create an array to hold all page contents in proper order
      const totalPages = currentBook.totalPages || 32;
      const contents = new Array(totalPages).fill("");
      
      // Extract text content from each spread's elements
      spreadKeys.forEach(key => {
        const spread = currentBook.spreads[key];
        if (spread && spread.elements && Array.isArray(spread.elements)) {
          // Find text elements in this spread
          spread.elements.forEach(element => {
            if (element.type === 'text' && element.content) {
              // If the element has a pageNumber property, use it
              if (element.pageNumber) {
                const pageIndex = element.pageNumber - 1;
                if (pageIndex >= 0 && pageIndex < contents.length) {
                  contents[pageIndex] = element.content;
                }
              }
              // Otherwise, use some other logic based on spreadId
              else {
                // Extract spread number
                const spreadNum = parseInt(key.split('-')[1]);
                // For simplicity, map spread-1 -> page 1 (index 0)
                const pageIndex = spreadNum - 1;
                if (pageIndex >= 0 && pageIndex < contents.length) {
                  contents[pageIndex] = element.content;
                }
              }
            }
          });
        }
      });
      
      console.log('Extracted page contents:', contents);
      setPageContents(contents);
      setShowPreview(true);
      setCurrentPage(1);
      return;
    }
    
    // Legacy fallback 2: If book has elements array but no prepared preview fields
    // if (currentBook.elements && Array.isArray(currentBook.elements)) {
    //   console.log('Loading preview data from book elements (legacy method):', currentBook.id);
    //   
    //   // Filter for text elements with odd-numbered pages (1, 3, 5, 7...)
    //   const textElements = currentBook.elements
    //     .filter(element => element.type === 'text' && element.pageNumber && element.pageNumber % 2 === 1)
    //     .sort((a, b) => a.pageNumber - b.pageNumber);
    //   
    //   console.log('Found text elements for odd-numbered pages:', textElements.map(el => el.pageNumber));
    //   
    //   // Only proceed if we have text elements
    //   if (textElements.length > 0) {
    //     // Create an array to hold all page contents
    //     const contents = new Array(currentBook.totalPages || 32).fill("");
    //     
    //     // Populate array with content from text elements
    //     textElements.forEach(element => {
    //       // Adjust the index (0-based array for 1-based page numbers)
    //       const index = element.pageNumber - 1;
    //       if (index >= 0 && index < contents.length) {
    //         contents[index] = element.content;
    //       }
    //     });
    //     
    //     console.log('Setting page contents from elements for pages:', textElements.map(el => el.pageNumber).join(', '));
    //     
    //     setPageContents(contents);
    //     setShowPreview(true);
    //     setCurrentPage(1);
    //     
    //     return; // Exit early if we successfully loaded from elements
    //   }
    // }
    // 
    // If no elements but book has pageContents, use those
    // if (currentBook.pageContents && currentBook.pageContents.length > 0) {
    //   console.log('Loading existing preview data from book pageContents:', currentBook.id);
    //   setPageContents(currentBook.pageContents);
    //   setShowPreview(true);
    //   setCurrentPage(1);
    // }
    
    // Initialize styles from current book
    if (currentBook.bookStyle || currentBook.visualStyle || currentBook.writingStyle) {
      setSelectedStyles({
        bookStyle: currentBook.bookStyle || null,
        visualStyle: currentBook.visualStyle || null,
        writingStyle: currentBook.writingStyle || null,
      });
    }
    
    // Set words per page from book
    if (currentBook.wordsPerPage) {
      setWordsPerPage(currentBook.wordsPerPage);
    }
    
    // If there's storyProgress data, set the status
    if (currentBook.storyProgress && currentBook.storyProgress.status) {
      setStoryProgressId(currentBook.storyProgress.id);
      setStoryProgressStatus(currentBook.storyProgress.status);
      setStoryProgressCreatedAt(currentBook.storyProgress.createdAt || null);
    }
  }, [currentBook]);

  // Handler for when story progress is complete
  const handleStoryProgressComplete = useCallback((progressData) => {
    try {
      console.log("Processing completed story progress:", progressData);

      if (!progressData.result || !progressData.result.Pages) {
        console.warn("Invalid story progress result - missing Pages array");
        
        // Try to get the book document again to see if it has been updated
        getBook(bookId).then(updatedBook => {
          // Check if the book has the required properties safely
          if (updatedBook && (updatedBook?.oddNumberedPagesForPreview || updatedBook?.previewPages)) {
            console.log("Book document has been updated with preview data, refreshing UI");
            // Use updateBook from the useBook context hook
            updateBook(updatedBook.id, updatedBook);
            
            // If oddNumberedPagesForPreview exists, create page contents array from it
            if (updatedBook?.oddNumberedPagesForPreview && Array.isArray(updatedBook.oddNumberedPagesForPreview)) {
              // Use optional chaining to safely access properties
              const contents = new Array(updatedBook?.totalPages || 32).fill("");
              
              updatedBook.oddNumberedPagesForPreview.forEach(page => {
                const index = page.pageNumber - 1;
                if (index >= 0 && index < contents.length) {
                  contents[index] = page.content;
                }
              });
              
              console.log('Using oddNumberedPagesForPreview data for preview display');
              setPageContents(contents);
              setShowPreview(true);
              setCurrentPage(1);
              setIsProcessing(false);
              return;
            }
          } else {
            console.error("No preview data found in book document");
            setIsProcessing(false);
            notify.error("Error retrieving preview data. Please try again.", { toastId: `edit-story-preview-error-${bookId}` });
          }
        }).catch(error => {
          console.error("Error fetching updated book:", error);
          setIsProcessing(false);
          notify.error("Error retrieving book data. Please try again.", { toastId: `edit-story-book-error-${bookId}` });
        });
        
        return;
      }

      // Calculate total pages
      const totalPagesCount = progressData.totalPages || 
                            (progressData.result.Pages.length * 2) || 
                            currentBook?.totalPages || 
                            32;
      
      // Prepare contents array
      const contents = new Array(totalPagesCount).fill("");

      // Fill the contents array with the story pages
      progressData.result.Pages.forEach((page) => {
        if (page.Page && page.Content) {
          const pageIndex = Math.floor(page.Page - 1);
          if (pageIndex < contents.length) {
            contents[pageIndex] = page.Content;
          }
        }
      });

      console.log(`Setting preview content from story progress result with ${progressData.result.Pages.length} pages`);
      
      // Set localStorage flags to trigger PreviewSection to refresh its list of all preview versions
      localStorage.setItem('needsPreviewRefresh', 'true');
      localStorage.setItem('lastPreviewSave', Date.now().toString());
      console.log('🔥🔥🔥 [useEditPage.js] Set localStorage flags to refresh preview list');
      
      setPageContents(contents);
      setShowPreview(true);
      setCurrentPage(1);
      setIsProcessing(false);
      
    } catch (error) {
      console.error("Error processing completed story:", error);
      notify.error("Error processing the generated story.", { toastId: `edit-story-process-error-${bookId}` });
      setIsProcessing(false);
    }
  }, [currentBook?.totalPages, bookId]);

  // Setup a listener for a story progress document
  const setupStoryProgressListener = useCallback((progressId) => {
    console.log(`Setting up listener for story progress: ${progressId}`);
    
    // Only set up a new listener if we don't already have one for this ID
    if (activeListenerRef.current && storyProgressId === progressId) {
      console.log(`Listener for ${progressId} already exists, skipping setup`);
      return activeListenerRef.current;
    }

    // Clear any existing listener
    if (activeListenerRef.current) {
      console.log('Cleaning up previous listener');
      activeListenerRef.current();
      activeListenerRef.current = null;
    }
    
    // Create new listener with includeMetadataChanges to catch all changes
    console.log(`🔥 CREATING FIREBASE LISTENER NOW for document: storyProgress/${progressId}`);
    console.log(`🔥 Firebase db object:`, !!db);
    console.log(`🔥 Document reference:`, doc(db, "storyProgress", progressId));
   
    const unsubscribe = onSnapshot(
      doc(db, "storyProgress", progressId),
      {
        includeMetadataChanges: true
      },
      (doc) => {
        const timestamp = new Date().toISOString();
        console.log(`🚨 [${timestamp}] *** FIREBASE LISTENER FIRED *** for ${progressId}:`, {
          exists: doc.exists(),
          fromCache: doc.metadata.fromCache,
          hasPendingWrites: doc.metadata.hasPendingWrites
        });
        
        if (doc.exists()) {
          loadingUIMessagesForStoryGeneration(doc.data().status);
          const data = doc.data();
          console.log('🔍 DOCUMENT STATUS CHECK:', {
            id: doc.id,
            status: data.status,
            hasResult: !!data.result,
            isProcessing: isProcessing
          });
          
          // Check if component is still mounted before updating state
          if (!isMountedRef.current) {
            console.log('Component unmounted, skipping state update');
            return;
          }
          
          // Capture draftedResult when status is outlineGenerated
          if (data.status === 'outlineGenerated' && data.draftedResult) {
            console.log('📝 Captured draftedResult from outlineGenerated status');
            setDraftedResult(data.draftedResult);
          }
          
          // DON'T exit loading state for documents that aren't explicitly completed or failed
          if (!data.status || (data.status !== 'completed' && data.status !== 'error' && data.status !== 'failed')) {
            console.log('🔄 Story is still in progress - keeping loading state active');
            setStoryProgressStatus(data.status || 'processing');
            setOperationType('preview');
            setIsProcessing(true);
            return;
          }
          
          // From here, we know we have a terminal state (completed, error, or failed)
          setStoryProgressStatus(data.status);
          setOperationType('preview'); // Always show story loading states for story progress
          console.log(`Story progress reached terminal status: ${data.status} for progress ${doc.id}`);
          
          if (data.status === "completed" && data.result) {
            console.log('✅ Story generation completed successfully with result');
            handleStoryProgressComplete(data);
            // Only show toast if one isn't already active
            notify.success("Preview generated successfully!", {
              toastId: 'preview-generated-toast'
            });
          } else if (data.status === "completed" && !data.result) {
            // Critical fix: Handle completed stories with null results
            console.error('❌ Story generation completed but has no result - exiting loading state');
            
            // Show error modal instead of toast
            showErrorModal(
              "Story Generation Failed", 
              "Your story was processed but no content was generated. This could be due to content restrictions or a technical issue. Please try again ."
            );
            
            setIsProcessing(false);
            setOperationType(null);
          } else if (data.status === "error" || data.status === "failed") {
            console.error('❌ Story generation failed with error:', data.error);
            
            // Show simple toast instead of modal
            notify.error(data.error || "Story generation failed. Please try again.", { toastId: `edit-story-error-${progressId}` });
            
            setIsProcessing(false);
          } else if (data.status === "pending" || data.status === "processing") {
            // Make sure we stay in processing state
            setIsProcessing(true);
            console.log('Story generation in progress - maintaining loading state');
          }
        }
      },
      (error) => {
        console.error(`💥 FIREBASE LISTENER ERROR for ${progressId}:`, error);
        console.error(`💥 Error code:`, error.code);
        console.error(`💥 Error message:`, error.message);
        notify.error("Error monitoring story progress", { toastId: `edit-story-listener-error-${progressId}` });
        setIsProcessing(false);
        
        // Clear the failed listener
        if (activeListenerRef.current) {
          activeListenerRef.current = null;
        }
      }
    );
    
    // Save the unsubscribe function
    activeListenerRef.current = unsubscribe;
    console.log(`Listener setup complete for ${progressId}`);
    return unsubscribe;
  }, [handleStoryProgressComplete, storyProgressId]);



  // Check for both preview processing and story progress when the component loads
  useEffect(() => {
    if (!bookId) return;
    
    // Setup preview processing listener first
    let previewUnsubscribe = () => {};
    if (!previewProcessingListenerRef.current) {
      checkForExistingPreviewProcessing().then(unsub => {
        previewUnsubscribe = unsub;
      });
    }
    
    // Force cleanup of any stale listeners and always check for story progress
    if (activeListenerRef.current) {
      console.log('🧹 Cleaning up stale story progress listener before new setup');
      activeListenerRef.current();
      activeListenerRef.current = null;
    }

    const checkForExistingStoryProgress = async () => {
      try {
        // Create a query to find any pending or processing story progress for this book
        const progressQuery = query(
          collection(db, "storyProgress"),
          where("bookId", "==", bookId),
          where("status", "in", ["pending","outlineGenerated","almostThere", "processing"]),
          orderBy("createdAt", "desc"),
          limit(1)
        );

        const querySnapshot = await getDocs(progressQuery);
        if (!querySnapshot.empty) {
          const progressDoc = querySnapshot.docs[0];
          const progressData = progressDoc.data();
          setStoryProgressId(progressDoc.id);
          setStoryProgressStatus(progressData.status);
          setStoryProgressCreatedAt(progressData.createdAt || null);
          console.log('checking existing storyProgress', progressData.status)
          setIsProcessing(true);
          setOperationType('preview'); // Always show story loading states for story progress
          
          console.log(`Found existing story progress: ${progressDoc.id} with status: ${progressData.status}`);
          
          // Setup real-time listener for this document THIS ONE RIGHT  HERE MARK THIS
          return setupStoryProgressListener(progressDoc.id);
        } else if (currentBook?.storyGenerationResult && !showPreview) {
          // If no active progress but we have stored results in the book
          console.log('Found stored story generation result in book');

          // Simulate a complete progress with the stored result
          handleStoryProgressComplete({
            result: currentBook.storyGenerationResult,
            totalPages: currentBook.totalPages
          });
        }
        return () => {}; // Return empty function if no progress found
      } catch (error) {
        console.error("Error checking for story progress:", error);
        return () => {}; // Return empty function in case of error
      }
    };

    // Start the async process and get the unsubscribe function
    let unsubscribe;
    checkForExistingStoryProgress().then(unsub => {
      unsubscribe = unsub;
    });
    
    // Clean up listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [bookId, handleStoryProgressComplete, currentBook, showPreview, setupStoryProgressListener]);

  // Status tracking functions already imported at the top
  
  // Setup a listener for a bookWithPreviewProcessing document
  const setupPreviewProcessingListener = useCallback((processingId) => {
    if (!processingId || typeof processingId !== 'string') return null;
    if (previewProcessingListenerRef.current) previewProcessingListenerRef.current(); // Clean up previous listener
    
    console.log(`Setting up preview processing listener for ${processingId}`);

    // Create new listener
    // Progress storySaveWithPreview toast messages are coming from these
    const unsubscribe = onSnapshot(
      doc(db, "bookWithPreviewProcessing", processingId),
      (doc) => {
        console.log(`Preview processing listener fired for ${processingId}:`, 
                  doc.exists() ? 'document exists' : 'document does not exist');
        
        if (doc.exists()) {
          const data = doc.data();
          console.log('Preview processing document data:', data.status);
          
          // Check if component is still mounted before updating state
          if (!isMountedRef.current) {
            console.log('Component unmounted, skipping preview processing state update');
            return;
          }
          
          // Update local state
          setPreviewProcessingCreatedAt(data.createdAt || null);
          
          // Update global status tracking (this will show toast notifications)
          if (bookId && data.status) {
            updateBookProcessingStatus(bookId, data.status);
          }
          
          // Show debug info
          console.log(`PREVIEW STATUS UPDATE: ${data.status} for book ${bookId}`);
          console.log('Full document data:', data);

          switch (data.status) {
          case "pending":
            setCanGoToDesign(false);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:5,text:'Preparing request...'});
            break;
          case "pending2":
            setCanGoToDesign(false);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:10,text:'Preparing request....'});
            break;
          case "processing":
            setCanGoToDesign(false);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:20,text:'Processing request to design...'});
            break;
          case "received":
            setCanGoToDesign(false);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:30,text:'Request Received...'});
            break;
          
          case "textElementsCreated":
            setCanGoToDesign(true);
            setOperationType('design');
            setIsProcessing(true);
            setLoadingUIMessage({progress:20,text:'Text elements created...'});
            console.log('Detected textElementsCreated status - navigating to /design');
            localStorage.setItem('NAVIGATION_DESIGN_BOOK_ID', bookId);
            setTimeout(() => {
              console.log('Navigating to design page');
              //navigate(`/design/${bookId}`);
            }, 250);
            break;
          case "initializing":
            setCanGoToDesign(true);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:40,text:'Initializing image generation...'});
            break;
          case "generatingCharacter":
            setCanGoToDesign(true);
            setOperationType('design');
            setIsProcessing(true);
            setLoadingUIMessage({progress:50,text:'Generating character description for story...'});
            break;
          case "generatingImagePrompts":
            setCanGoToDesign(true);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:65,text:'Generating image prompts...'});
            break;
          case "generatingCoverImage":
            setCanGoToDesign(true);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:80,text:'Generating cover image...'});
            break;
          case "updatingImageElements":
            setCanGoToDesign(true);
            setOperationType('design');
            setIsProcessing(true); // Ensure loading state is active
            setLoadingUIMessage({progress:85,text:'Updating image elements...'});
            break;
          case "error":
            setCanGoToDesign(false);
            setOperationType('design');
            setLoadingUIMessage({progress:100,text:'Request Failed...'});
            setIsProcessing(false);
            // Show a temporary local toast that doesn't persist
            notify.error("Request Failed!", { toastId: `edit-preview-error-${bookId}` });
            // CLEAR any toast flags that might have been set
            localStorage.removeItem('CRITICAL_DESIGN_READY_TOAST');
            localStorage.removeItem('CRITICAL_DESIGN_READY_MESSAGE');
            localStorage.removeItem('CRITICAL_DESIGN_READY_TIMESTAMP');
            localStorage.removeItem('CRITICAL_BOOK_ID');
            localStorage.removeItem('showDesignReadyToast');
            

            break;
          case "cancelled":
            setCanGoToDesign(false);
            setOperationType('design');
            setLoadingUIMessage({progress:100,text:'Request Cancelled...'});
            setIsProcessing(false);
            break;
          case "completed":
            setCanGoToDesign(true);
            setLoadingUIMessage({progress:100,text:'Request Completed...'});
            setIsProcessing(false);
            // Show a temporary local toast that doesn't persist
            notify.success("Book preview generated successfully!", { toastId: `edit-preview-complete-${bookId}` });
            // CLEAR any toast flags that might have been set
            localStorage.removeItem('CRITICAL_DESIGN_READY_TOAST');
            localStorage.removeItem('CRITICAL_DESIGN_READY_MESSAGE');
            localStorage.removeItem('CRITICAL_DESIGN_READY_TIMESTAMP');
            localStorage.removeItem('CRITICAL_BOOK_ID');
            localStorage.removeItem('showDesignReadyToast');
            
            // Navigate to world step page without setting toast flags - book is already complete
            setTimeout(() => refreshBookThenNavigate(), 200);
            unsubscribe();
            previewProcessingListenerRef.current = null;
            break;

          default :
            setLoadingUIMessage({progress:0,text:'...'});
            break;
          };
        }
      },
      (error) => {
        console.error(`Error in preview processing listener for ${processingId}:`, error);
        setIsProcessing(false); // Stop loading on error
        notify.error("Error monitoring preview generation progress", { toastId: `edit-preview-listener-error-${processingId}` });
      }
    );
    
    // Save the unsubscribe function
    previewProcessingListenerRef.current = unsubscribe;
    console.log(`Preview processing listener setup complete for ${processingId}`);
    return unsubscribe;
  }, [db, navigate, bookId, setIsProcessing, updateBookProcessingStatus]);

  // Clean up active listeners when component unmounts
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (activeListenerRef.current) {
        console.log('Cleaning up active story progress listener on unmount');
        activeListenerRef.current();
        activeListenerRef.current = null;
      }
      if (previewProcessingListenerRef.current) {
        console.log('Cleaning up active preview processing listener on unmount');
        previewProcessingListenerRef.current();
        previewProcessingListenerRef.current = null;
      }
    };
  }, []);

  // Function to check for existing preview processing documents
  const checkForExistingPreviewProcessing = useCallback(async () => {
    try {
      console.log(`Checking for existing preview processing for book ${bookId}`);
      // Query for pending/processing preview documents for this book
      const processingQuery = query(
        collection(db, "bookWithPreviewProcessing"),
        where("bookId", "==", bookId),
        where("status", "not-in", ["completed", "error", "failed", "cancelled"]),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const querySnapshot = await getDocs(processingQuery);
      
      if (!querySnapshot.empty) {
        const processingDoc = querySnapshot.docs[0];
        const processingData = processingDoc.data();
        
        console.log(`Found existing preview processing: ${processingDoc.id} with status: ${processingData.status}`);
        
        setPreviewProcessingId(processingDoc.id);
        setPreviewProcessingCreatedAt(processingData.createdAt || null);
        setIsProcessing(true); // Activate loading state
        setOperationType('design'); // Show story loading states for preview processing
        
        // Setup real-time listener for this document
        return setupPreviewProcessingListener(processingDoc.id);
      }
      return () => {}; // Return empty function if no processing found
    } catch (error) {
      console.error("Error checking for preview processing:", error);
      return () => {};
    }
  }, [bookId, setupPreviewProcessingListener]);

  // Check for existing preview processing when the component loads
  useEffect(() => {
    if (!bookId) return;
    
    // Skip if we already have an active listener
    if (previewProcessingListenerRef.current) {
      console.log('Skip preview processing setup - already have active preview processing listener');
      return;
    }

    // Start the async process and get the unsubscribe function
    let unsubscribe;
    checkForExistingPreviewProcessing().then(unsub => {
      unsubscribe = unsub;
    });
    
    // Clean up listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [bookId, checkForExistingPreviewProcessing]);

  // Restore the loading overlay when returning to /edit while a design operation
  // is still active according to BookContext (covers the window where the
  // processing document doesn't exist yet, e.g. updateBookWithPreviewOne in flight)
  useEffect(() => {
    if (!previewProcessingStatus || !ACTIVE_DESIGN_STATUSES.has(previewProcessingStatus)) return;
    if (!isProcessing) {
      setOperationType('design');
      setIsProcessing(true);
    }
    // If the processing document appeared after the initial on-mount check,
    // attach the listener now so completion/error still get handled
    if (!previewProcessingListenerRef.current) {
      checkForExistingPreviewProcessing();
    }
  }, [previewProcessingStatus, isProcessing, checkForExistingPreviewProcessing]);

  const pollWebhookStatus = useCallback(
    async (values) => {
      if (!webhookId) return;

      try {
        const result = await checkStoryStatus(webhookId);
        if (result.status === "completed") {
          // Split content into pages
          const paragraphs = result.content.split("\n\n");
          const contents = new Array(parseInt(values.totalPages, 10)).fill("");

          paragraphs.forEach((paragraph, index) => {
            const pageIndex = index * 2;
            if (pageIndex < contents.length) {
              contents[pageIndex] = paragraph.trim();
            }
          });

          setPageContents(contents);
          setShowPreview(true);
          setCurrentPage(1);
          setWebhookId(null);
        } else if (result.status === "failed") {
          throw new Error(result.error || "Story generation failed");
        } else {
          // Continue polling after 2 seconds
          setTimeout(() => pollWebhookStatus(values), 2000);
        }
      } catch (error) {
        console.error("Error checking webhook status:", error);
        if (error.code === "permission-denied") {
          // Fall back to original content if auth fails
          const contents = new Array(parseInt(values.totalPages, 10)).fill("");
          contents[0] = values.storyContent;
          setPageContents(contents);
          setShowPreview(true);
          setCurrentPage(1);
        }
        setIsProcessing(false);
      }
    },
    [webhookId]
  );

  const handleGeneratePreview = useCallback(
    async (values) => {
      setLoadingUIMessage({progress:0,text:"Generating your story..."})
      setOperationType('preview');
      setDraftedResult(null); // Clear old preview when starting new generation
      if (!values.storyContent?.trim()) return;

      try {
        notify.info("Generating your story...", { toastId: `edit-story-start-${bookId}` });
        setIsProcessing(true);

        if (!isDummyDataEnabled) {
          // Validate required values
          if (!values.storyContent) {
            notify.error("Please enter a story content", { toastId: `edit-validation-content-${bookId}` });
            setIsProcessing(false);
            return;
          }
          if (!values.totalPages) {
            notify.error("Please specify total pages", { toastId: `edit-validation-pages-${bookId}` });
            setIsProcessing(false);
            return;
          }
          if (!selectedStyles.writingStyle?.title) {
            notify.error("Please select a writing style", { toastId: `edit-validation-style-${bookId}` });
            setIsProcessing(false);
            return;
          }
          if (!bookId) {
            notify.error("Book ID is missing", { toastId: `edit-validation-bookid` });
            setIsProcessing(false);
            return;
          }

          // Use real API
          console.log('Generating story with:', {
            storyContent: values.storyContent,
            wordsPerPage: wordsPerPageValue,
            totalPages: values.totalPages,
            writingStyle: selectedStyles.writingStyle?.title,
            bookId
          });

          const result = await generateWholeStory(
            values.storyContent,
            wordsPerPageValue,
            values.totalPages,
            selectedStyles.writingStyle?.title,
            bookId
          );

          console.log('generateWholeStory result:', JSON.stringify(result, null, 2));

          if (result.progressId) {
            console.log(`Story progress created with ID: ${result.progressId}`);
            setStoryProgressId(result.progressId);
            setStoryProgressStatus("pending");
            setStoryProgressCreatedAt(new Date().toISOString());
            setOperationType('preview'); // Set operation type to preview since this is from preview generation
            
            // This is the key change - immediately setup a listener for this progress
            console.log('Setting up listener after receiving progressId');
            setupStoryProgressListener(result.progressId);
            
            // Keep the loading UI active - DO NOT set isProcessing to false here
            notify.info("Story generation has started. This may take a few minutes. Please wait while we prepare your preview...", { toastId: `edit-story-started-${result.progressId}` });
            
            // Force a check of the progress document now to make sure we have it
            try {
              const progressDoc = await getDoc(doc(db, "storyProgress", result.progressId));
              if (progressDoc.exists()) {
                console.log(`Immediate check of progress document: ${JSON.stringify(progressDoc.data(), null, 2)}`);
              } else {
                console.warn(`Progress document ${result.progressId} does not exist yet`);
              }
            } catch (error) {
              console.error('Error checking progress document:', error);
            }
            
            return; // Exit early to maintain loading state
          } else if (result.webhookId) {
            setWebhookId(result.webhookId);
            pollWebhookStatus(values);
          } else {
            const messageText =
              result?.data?.message[1]?.text || result?.data?.message[0]?.text;

            if (!messageText) {
              notify.error("Something went wrong, please try again...", { toastId: `edit-preview-error-${bookId}` });

              setIsProcessing(false);
              return;
            }

            // Parse the JSON response from the second message
            const pagesData = JSON.parse(messageText);
            const contents = new Array(parseInt(values.totalPages, 10)).fill("");

            // Fill the contents array with the story pages
            pagesData.Pages.forEach((page) => {
              const pageIndex = Math.floor(page.Page - 1);
              if (pageIndex < contents.length) {
                contents[pageIndex] = page.Content;
              }
            });

            setPageContents(contents);
            setShowPreview(true);
            setCurrentPage(1);
          }
        } else {
          // Use dummy data for preview
          const dummyContents = new Array(parseInt(values.totalPages, 10)).fill("");
          dummyContents[0] = values.storyContent;
          setPageContents(dummyContents);
          setShowPreview(true);
          setCurrentPage(1);
        }
        setIsProcessing(false);
      } catch (error) {
        console.error("Error generating preview:", error);
        notify.error("Failed to generate preview. Please try again.", { toastId: `edit-preview-error-${bookId}` });
        setIsProcessing(false);
      }
    },
    [isDummyDataEnabled, wordsPerPageValue, pollWebhookStatus, bookId, setupStoryProgressListener, selectedStyles.writingStyle]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleContentChange = useCallback((pageIndex, newContent) => {
    setPageContents((prev) => {
      const newContents = [...prev];
      newContents[pageIndex] = newContent;
      return newContents;
    });
  }, []);

  const handleSaveBasicInfo = useCallback(
    async (values) => {
      try {
        setIsSavingBasicInfo(true);
        const updates = {
          title: values.title,
          bookStyle: selectedStyles.bookStyle,
          visualStyle: selectedStyles.visualStyle,
          writingStyle: selectedStyles.writingStyle,
          wordsPerPage: wordsPerPageValue,
          totalPages: parseInt(values.totalPages, 10),
          storyContent: values.storyContent,
          pageSize: values.pageSize, // Add pageSize from form values
          updatedAt: new Date().toISOString()
        };
        
        // Also update local storage for page size
        if (values.pageSize) {
          localStorage.setItem(`book_page_size_${bookId}`, values.pageSize);
          console.log(`Saving page size ${values.pageSize} to localStorage for book ${bookId}`);
        }

        await updateBook(bookId, updates);
        notify.success("Basic information saved successfully!", { toastId: `edit-save-basic-${bookId}` });
      } catch (error) {
        console.error("Error saving basic info:", error);
        notify.error("Failed to save basic information. Please try again.", { toastId: `edit-save-basic-error-${bookId}` });
      } finally {
        setIsSavingBasicInfo(false);
      }
    },
    [bookId, selectedStyles, wordsPerPageValue, updateBook]
  );

  // ─── DESIGN SAVE FLOW ────────────────────────────────────────────
  // Updates only text content in existing spread elements (preserving layout),
  // then regenerates world/characterData. Used when saving from design preview.
  const handleDesignSave = useCallback(
    async (previewContents, values) => {
      console.log('🎨 DESIGN SAVE: Starting design-specific save flow');
      try {
        setLoadingUIMessage({ progress: 0, text: "Saving design changes..." });
        setOperationType('design');
        setIsProcessing(true);

        // Persist basic info first
        try {
          const updates = {
            title: values.title,
            bookStyle: selectedStyles.bookStyle,
            visualStyle: selectedStyles.visualStyle,
            writingStyle: selectedStyles.writingStyle,
            wordsPerPage: wordsPerPageValue,
            totalPages: parseInt(values.totalPages, 10),
            storyContent: values.storyContent,
            pageSize: values.pageSize,
            updatedAt: new Date().toISOString()
          };
          if (values.pageSize) {
            localStorage.setItem(`book_page_size_${bookId}`, values.pageSize);
          }
          await updateBook(bookId, updates);
        } catch (e) {
          console.error('Error saving basic info before design save:', e);
        }

        // Extract content with element IDs from design content
        // Design content should be arrays of element objects with IDs preserved
        const safeContents = Array.isArray(previewContents) ? previewContents : [];
        const contentWithIds = [];
        
        safeContents.forEach(pageContent => {
          if (!pageContent) return;
          
          if (typeof pageContent === 'string') {
            // Legacy string content - no ID available
            contentWithIds.push({ content: pageContent, id: null });
          } else if (Array.isArray(pageContent)) {
            // Array of elements - extract text elements with their IDs
            const textElements = pageContent
              .filter(el => el && el.type !== 'image' && el.content)
              .map(el => ({ content: el.content, id: el.id || null }));
            
            // Push each text element as a separate entry with its ID
            textElements.forEach(textEl => {
              contentWithIds.push(textEl);
            });
          }
        });

        console.log('🎨 DESIGN SAVE: Extracted content with IDs for', contentWithIds.filter(c => c.content).length, 'text elements');

        // Call updateDesignTextElements (Function 1)
        const functions = getFunctions();
        const updateDesignFunc = httpsCallable(functions, 'updateDesignTextElements');

        setLoadingUIMessage({ progress: 30, text: "Updating text elements..." });

        const result = await updateDesignFunc({
          bookId,
          pageContents: contentWithIds
        });

        console.log('🎨 DESIGN SAVE: Text elements updated, result:', result.data);
        
        // Character generation is now fire-and-forget via generateMainCharacter
        // The Flux API will call back to saveCharacterDataFromDesign when done
        // No need to wait or call saveCharacterDataFromDesign manually
        
        setLoadingUIMessage({ progress: 100, text: "Done!" });
        notify.success("Design changes saved successfully! Character data is being generated.", { toastId: `edit-design-save-${bookId}` });

        // Navigate to design page
        navigate(`/design/${bookId}`);

      } catch (error) {
        console.error("🎨 DESIGN SAVE ERROR:", error);
        const errorMessage = error.code === 'unauthenticated'
          ? "You must be logged in to save changes."
          : error.code === 'invalid-argument'
          ? "Invalid book data. Please check your entries and try again."
          : "Failed to save design changes. Please try again.";
        notify.error(errorMessage, { toastId: `edit-design-save-error-${bookId}` });
      } finally {
        setIsProcessing(false);
        setOperationType(null);
        setLoadingUIMessage({ progress: 0, text: 'Ready' });
      }
    },
    [bookId, navigate, selectedStyles, wordsPerPageValue, updateBook]
  );

  // ─── STORYPROGRESS SAVE FLOW ──────────────────────────────────────
  // Existing flow: overwrites design with updateBookWithPreviewOne/Two.
  // Used when saving from storyProgress preview (not design).
  const handleSavePreview = useCallback(
    async (previewContents, values, isDesignMode) => {
      setDraftedResult(null); // Clear old preview when saving new preview
      console.log('DEBUG: handleSavePreview function called with:', { 
        title: values?.title,
        bookId: bookId,
        totalPages: values?.totalPages,
        isDesignMode,
        hasPreviewContents: Array.isArray(previewContents) && previewContents.length > 0,
        previewContentsLength: Array.isArray(previewContents) ? previewContents.length : 0,
        internalContentsLength: Array.isArray(pageContents) ? pageContents.length : 0
      });

      // BRANCH: If saving from design mode, use the design-specific save flow
      if (isDesignMode) {
        console.log('🎨 Routing to design save flow');
        return handleDesignSave(previewContents, values);
      }

      // Otherwise, use the existing storyProgress save flow
      console.log('📚 Using storyProgress save flow (updateBookWithPreviewOne)');
      try {
        setLoadingUIMessage({progress:0,text:"Generating your preview, Please don't close the browser..."})
        setOperationType('design');
        setIsProcessing(true);
        // Optimistically mark processing as pending in BookContext so the global
        // overlay on /world and /design stays visible while updateBookWithPreviewOne
        // is still in flight (before the processing document exists in Firestore)
        setProcessingStatus('pending');
        setGlobalLoadingUIMessage({progress: 5, message: 'Preparing your design...'});
        console.log('DEBUG: Setting isProcessing to true and operationType to design');
        console.log('DEBUG: About to call updateBookWithPreviewOne Firebase function');
        
        // FIRST: Persist basic information changes before overwriting design
        try {
          const updates = {
            title: values.title,
            bookStyle: selectedStyles.bookStyle,
            visualStyle: selectedStyles.visualStyle,
            writingStyle: selectedStyles.writingStyle,
            wordsPerPage: wordsPerPageValue,
            totalPages: parseInt(values.totalPages, 10),
            storyContent: values.storyContent,
            pageSize: values.pageSize,
            updatedAt: new Date().toISOString()
          };
          if (values.pageSize) {
            localStorage.setItem(`book_page_size_${bookId}`, values.pageSize);
          }
          await updateBook(bookId, updates);
        } catch (e) {
          console.error('Error saving basic info before preview generation:', e);
        }
        
        // Convert hybrid structure back to simple strings for story mode
        const convertToSimpleStrings = (contents) => {
          if (!Array.isArray(contents)) return contents;
          
          return contents.map(page => {
            // If it's the hybrid structure (array of elements), convert to string
            if (Array.isArray(page)) {
              return page.map(element => 
                typeof element === 'string' ? element : (element.content || '')
              ).join('\n');
            }
            // If it's already a string, keep it
            return typeof page === 'string' ? page : '';
          });
        };
        
        const simplePageContents = convertToSimpleStrings(previewContents);
        
        const functions = getFunctions();
        const updateBookWithPreviewOneFunc = httpsCallable(functions, 'updateBookWithPreviewOne');
        
        const requestData = {
          bookId,
          bookData: {
            title: values.title,
            bookStyle: selectedStyles.bookStyle,
            visualStyle: selectedStyles.visualStyle,
            writingStyle: selectedStyles.writingStyle,
            wordsPerPage: wordsPerPageValue,
            totalPages: parseInt(values.totalPages, 10),
            storyContent: values.storyContent,
            pageContents: simplePageContents,
            overwriteDesign: true,
            step: 1
          }
        };
        
        console.log('Calling with data:', requestData);
        console.log('DEBUG: Making API call with this data:', JSON.stringify(requestData, null, 2));
        const result = await updateBookWithPreviewOneFunc(requestData);

        console.log('DEBUG: API call completed, result:', JSON.stringify(result.data, null, 2));
        
        notify.success("Story text added. World details and image placeholders coming next.", {
          toastId: `edit-save-preview-${bookId}`,
        });
        // Don't navigate immediately - instead try to find the preview processing document
        const processingRef = collection(db, "bookWithPreviewProcessing");
        const q = query(
          processingRef,
          where("bookId", "==", bookId),
          where("status", "not-in", ["completed", "error", "cancelled"]),
          orderBy("createdAt", "desc"),
          limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const processingDoc = querySnapshot.docs[0];
          console.log(`Found preview processing document after save: ${processingDoc.id}`);
          setPreviewProcessingId(processingDoc.id);
          setPreviewProcessingCreatedAt(processingDoc.data().createdAt || null);
          setupPreviewProcessingListener(processingDoc.id);
          setOperationType('design');
        } else {
          console.log('No preview processing document found after save, will navigate with notification state');
          setIsProcessing(false);
        }
      } catch (error) {
        console.error("Error calling function:", error);
        console.error("Full error details:", {
          code: error.code,
          message: error.message,
          details: error.details
        });
        
        // Clean up any stuck processing documents
        try {
          const processingRef = collection(db, "bookWithPreviewProcessing");
          const q = query(
            processingRef,
            where("bookId", "==", bookId),
            where("status", "==", "pending"),
            orderBy("createdAt", "desc"),
            limit(1)
          );
          
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const processingDoc = querySnapshot.docs[0];
            console.log('Cleaning up stuck processing document:', processingDoc.id);
            await updateDoc(processingDoc.ref, {
              status: 'error',
              error: 'Error occurred during save - Firebase function failed',
              updatedAt: new Date().toISOString()
            });
          }
        } catch (cleanupError) {
          console.error('Error cleaning up processing document:', cleanupError);
        }
        
        const errorMessage = error.code === 'unauthenticated' 
          ? "You must be logged in to save changes."
          : error.code === 'invalid-argument'
          ? "Invalid book data. Please check your entries and try again."
          : "Failed to save changes. Please try again.";
        
        notify.error(errorMessage, { toastId: `edit-save-preview-error-${bookId}` });
        
        setProcessingStatus(null);
        setIsProcessing(false);
        setOperationType(null);
        setPreviewProcessingId(null);
        setPreviewProcessingCreatedAt(null);
        setLoadingUIMessage({progress: 0, text: 'Ready'});
      }
    },
    [bookId, navigate, selectedStyles, wordsPerPageValue, setupPreviewProcessingListener, pageContents, handleDesignSave, setProcessingStatus, setGlobalLoadingUIMessage]
  );

  const handleContinueWithoutSave = useCallback(() => {
    navigate(`/design/${bookId}`);
  }, [bookId, navigate]);

  const goToDesignRoute = useCallback(() => {
    navigate(`/design/${bookId}`); }, [bookId, navigate]);
  return {
    showPreview,
    setShowPreview,
    currentPage,
    pageContents,
    setPageContents,
    selectedStyles,
    setSelectedStyles,
    isProcessing,
    setIsProcessing,
    storyProgressStatus,
    handleStyleSelect,
    handleGeneratePreview,
    operationType,
    setOperationType,
    handlePageChange,
    handleContentChange,
    handleSaveBasicInfo,
    handleSavePreview,
    handleContinueWithoutSave,
    wordsPerPage,
    setWordCount: setWordsPerPage,
    showErrorModal,
    loadingUIMessage,
    canGoToDesign,
    goToDesignRoute,
    previewProcessingId,
    previewProcessingStatus,
    canCancelProcessing,
    handleCancelProcessing,
    draftedResult,
    isSavingBasicInfo,
    overlayDefaultMinimized: designActiveAtMountRef.current
  };
}
